<template>
  <div class="pay">
    <h1 class="boxTitle mb15 notranslate">{{locale.order_payment}}</h1>
    <!-- tip -->
    <div v-if="tips && step === 'tip'" class="tips notranslate">
      <p v-if="!payMethod" class="payMessage">{{locale.title_select_tip}}</p>
      <div class="mt30">
        <div class="tipBox">
          <div v-for="(t, index) in tips" :key="index" class="checkbox tipUnit">
            <input type="radio" name="tips" :id="`tip_${index}`" :value="t" v-model="tip">
            <label :for="`tip_${index}`">
              {{tipLabel(t)}}
              <span>{{priceFormat(tipPrice(t))}}</span>
            </label>
          </div>
        </div>
        <div class="tipBox">
          <div class="checkbox tipUnit">
            <input type="radio" name="tips" id="tip_custom" value="custom" v-model="tip">
            <label for="tip_custom">{{locale.direct_input}}</label>
          </div>
          <div class="checkbox tipUnit">
            <input type="radio" name="tips" id="tip_notip" value="notip" v-model="tip">
            <label for="tip_notip">{{locale.notip}}</label>
          </div>
        </div>
        <div v-if="tip === 'custom'" class="mt15">
          <div class="custom">
            <input type="number" class="input sizeM" v-model="tipInput" @input="customTip">
            <span class="checkbox">
              <input type="radio" name="tip_unit" id="tip_unit_percent" value="percent" v-model="tipUnit">
              <label for="tip_unit_percent">%</label>
            </span>
            <span class="checkbox">
              <input type="radio" name="tip_unit" id="tip_unit_price" value="price" v-model="tipUnit">
              <label for="tip_unit_price">{{currencyEntity}}</label>
            </span>
          </div>
        </div>
        <div v-if="tip === 'custom' && tipInput && tipUnit === 'percent'" class="mt5 customTipPrice">
          <span>{{priceFormat(tipPrice(tipInput))}}</span>
        </div>
        <p v-if="tip === 'custom'" class="sysMessage colorGrey mt5">* {{locale.message_use_period}}</p>
        <p v-if="tax && settingOrder.tipIncludeTax" class="sysMessage colorGrey mt5">* {{locale.message_tip_include_tax}}</p>
      </div>
      <div class="mt30">
        <button type="button" class="btn btnFull btnRound" :disabled="!tipValue" @click="changeStep('payMethod')">{{locale.select_pay_method}}</button>
      </div>
    </div>
    <!-- //tip -->
    <!-- payment method -->
    <div v-else-if="step === 'payMethod'" :class="{activeMessage: orderMessage}">
      <p v-if="!payMethod" class="payMessage notranslate">{{locale.payment_step_message}}</p>
      <div class="payMethod notranslate">
        <button type="button"
          class="btnMethod btnPg"
          @click.stop="submit('pg')">
          {{locale.payment_pg}}
        </button>
        <button
          v-if="supportRoomCharge"
          type="button"
          class="btnMethod btnRoomcharge"
          :class="{disabled: noPost || (order && order.disabledRoomCharge)}"
          :disabled="noPost || (order && order.disabledRoomCharge)"
          @click.stop="submit('roomCharge')">
          {{locale.payment_roomcharge}}<span>({{locale.roomcharge}})</span>
        </button>
      </div>
      <div v-if="tips && !order.disabledTax" class="mt30">
        <button type="button" class="btn btnRound btnWhite btnFull" @click="changeStep('tip')">{{locale.select_pay_tip}}</button>
      </div>
      <!-- 룸차지시 안내문구 -->
      <div v-if="roomchargeMessage && orderMessage" class="orderMessage">
        <div class="message">
          {{roomchargeMessage[lang]}}
        </div>
        <div class="btns mt30 notranslate">
          <!-- <button type="button" class="btn btnWhite toCapitalize" @click="orderMessage = false">{{locale.cancel}}</button> -->
          <p class="pleaseConfirm">{{locale.order_pre_confirm_message}}</p>
          <button type="button" class="btn btnRound btnFull toCapitalize" @click="setRoomCharge">{{locale.next_step}}</button>
        </div>
      </div>
      <!-- 룸차지시 안내문구 -->
    </div>
    <!-- //payment method -->
    <!-- payMethod:: pg 일반결제/해외결제 선택 -->
    <div v-if="paymentOptionSelector && supportPgList && supportPgList.length" class="layer payOptionLayer">
      <button type="button" class="btnClose" @click.stop="paymentOptionSelector = false"></button>
      <div class="inner">
        <p class="layerTitle">{{ locale.select_payment_type }}</p>
        <div class="btns">
          <button
            v-for="pg in supportPgList"
            :key="pg.pgType"
            type="button"
            class="btnPg"
            :data-type="pg.pgType"
            :data-simple="pg.pgType && pg.pgType.includes('SIMPLE')"
            @click.stop="setPg(pg.pgType)">
            {{locale[pg.name]}}
          </button>
        </div>
      </div>
    </div>
    <!-- //payMethod:: pg 일반결제/해외결제 선택 -->
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import regExp from '@/common/regExp'

export default {
  name: 'PaymentStep',
  props: [
    'lang',
    'locale'
  ],
  computed: {
    ...mapGetters({
      getTax: 'payment/getTax'
    }),
    currencyCode () {
      return this.$store.getters['common/getSetting'].currency.toUpperCase()
    },
    currencyEntity () {
      return this.$store.getters['common/getSetting'].currencyEntity
    },
    tax () {
      return this.$store.state.payment.tax
    },
    siteInfo () {
      return this.$store.getters['getSiteInfo']
    },
    settingOrder () {
      return this.$store.getters['common/getSetting'].order
    },
    supportRoomCharge () {
      return this.siteInfo && this.siteInfo.roomCharge
    },
    roomchargeMessage () {
      const setting = this.settingOrder
      return setting && setting.roomChargeMsgActive ? setting.roomChargeMsg : null
    },
    tips () {
      return this.$store.state.payment.tip
        ? this.$store.state.payment.tip.filter(x => x !== 'custom' && x !== 'notip').map(x => Number(x))
        : null
    },
    tipValue () {
      let tip = this.tip
      let unit = 'percent'
      let price = 0
      if (tip === 'custom') {
        tip = Number(this.tipInput)
        unit = this.tipUnit
        price = this.tipUnit === 'percent'
          ? this.tipPrice(this.tipInput)
          : regExp.priceUnFormat(this.tipInput)
      } else if (tip === 'notip') {
        tip = 0
        price = 0
      } else {
        tip = Number(this.tip)
        price = this.tipPrice(tip)
      }
      return this.tip === ''
        ? null
        : {
          value: tip,
          unit: unit,
          price: price
        }
    }
  },
  created () {
    this.init()
  },
  data () {
    return {
      order: null,
      payMethod: null,
      orderMessage: false,
      tip: '',
      tipInput: '15',
      tipUnit: 'percent',
      step: 'payMethod',
      paymentOptionSelector: false,
      supportPgList: [],
      noPost: false
    }
  },
  methods: {
    async init () {
      const guestUserInfo = this.$store.getters['guest/getGuestUserInfo']
      this.noPost = (guestUserInfo && guestUserInfo.noPost) || false
      this.step = 'payMethod'
      this.tip = ''
      this.tipInput = '15'
      this.tipUnit = 'percent'
      if (this.$store.state.payment.tip) {
        this.step = 'tip'
      }
      const store = this.$store.getters['payment/getOrder']
      const order = store
      const taxPer = this.tax && order && order.disabledTax !== true
      let grandTotal = 0
      if (order) {
        this.order = order && order.payload
          ? {
            ...order,
            payload: order.payload
          }
          : order
        if (this.order.price) {
          if (taxPer) {
            this.order.price.info.tax = this.getTax(this.order.price.info.subtotal || this.order.price.info.total)
            this.order.price.info.taxLabel = `${this.tax}%`
          }
          this.order.price.info.total = this.order.price.info.subtotal || this.order.price.info.total
          this.order.price.info.subtotal = this.order.price.info.subtotal || this.order.price.info.total
          grandTotal = this.order.price && this.order.price.info && this.order.price.info.total
            ? this.order.price.info.subtotal + this.order.price.info.tax || this.order.price.info.total
            : 0
          this.order.price = {
            ...this.order.price,
            info: {
              ...this.order.price.info,
              total: grandTotal
            },
            total: grandTotal
          }
        }
        if (this.order.payload) {
          this.order.payload = this.order.payload.filter(x => x.submitId !== 'tip').map(x => {
            const subtotal = x.price.info.subtotal || x.price.info.total
            const tax = this.getTax(subtotal)
            const total = subtotal + tax
            return {
              ...x,
              ...x.pIdInfo && {
                ...x.pIdInfo,
                items: x.pIdInfo.items.map(i => {
                  const pidSubtotal = i.subtotal || i.total
                  const pidTax = this.getTax(pidSubtotal)
                  const pidTotal = pidSubtotal + pidTax
                  return {
                    ...i,
                    ...taxPer && {
                      subtotal: pidSubtotal,
                      tax: pidTax,
                      taxLabel: `${this.tax}%`,
                      total: pidTotal
                    }
                  }
                }),
                price: {
                  ...x.pIdInfo.price,
                  ...taxPer && {
                    subtotal: x.pIdInfo.price.subtotal || x.pIdInfo.price.total,
                    tax: this.getTax(x.pIdInfo.price.subtotal || x.pIdInfo.price.total),
                    taxLabel: `${this.tax}%`,
                    total: (x.pIdInfo.price.subtotal || x.pIdInfo.price.total) + this.getTax(x.pIdInfo.price.subtotal)
                  }
                }
              },
              ...x.price && {
                price: {
                  ...x.price,
                  info: {
                    ...x.price.info,
                    ...taxPer && {
                      subtotal: subtotal,
                      tax: tax,
                      taxLabel: `${this.tax}%`,
                      total: total
                    }
                  },
                  ...x.price.option && {
                    option: x.price.option.map(o => {
                      const subtotal = o.price * o.quantity
                      const tax = this.getTax(subtotal)
                      const total = subtotal + tax
                      return {
                        ...o,
                        subtotal: subtotal,
                        tax: tax,
                        taxLabel: `${this.tax}%`,
                        total: total
                      }
                    })
                  }
                }
              }
            }
          })
        }
        if (order && order.disabledTax === true) {
          this.tip = 'notip'
          this.changeStep('payMethod')
        }
        this.$store.dispatch('payment/supportPgInfo', { catCode: this.order.payload[0].catCode }).then((res) => {
          const supportPg = res.supportPGInfo
          this.supportPgList = Object.keys(supportPg).map(x => {
            return {
              pgType: x,
              name: `pg_type_${x}`,
              ...supportPg[x]
            }
          })
        })
      } else {
        alert(this.locale.alert_payment_info_not_found)
        this.$router.push({ name: 'main', params: { ln: this.$route.params.ln } })
      }
    },
    setMethod (method) {
      this.payMethod = method
    },
    changeStep (method) {
      this.step = method
      if (method === 'payMethod') {
        const notip = this.tip === 'notip'
        const tip = notip ? 0 : this.tipValue.price
        const tipUnit = this.tipValue.unit === 'percent' ? `${this.tipValue.value}%` : ''
        const tipLabel = tipUnit
        const tipIncludeTax = this.tax && this.settingOrder.tipIncludeTax
        const clone = this._.cloneDeep(this.order.payload[0])
        const langObj = this.$store.state.common.setting.lang.info
        const subject = function (langObj, val) {
          let obj = {}
          langObj.map(x => {
            obj[x.c] = val || ''
          })
          return obj
        }
        this.order.price = {
          ...this.order.price,
          info: {
            ...this.order.price.info,
            tip: tip,
            tipLabel: tipLabel,
            tipIncludeTax: tipIncludeTax,
            total: this.order.price.info.total + tip
          },
          total: this.order.price.info.total + tip
        }
        if (notip) {
          delete this.order.price.info.tip
          delete this.order.price.info.tipLabel
          delete this.order.price.info.tipInclueTax
        } else {
          this.order.payload = this.order.payload.filter(x => x.submitId !== 'tip').concat({
            title: 'tip',
            catCode: 'tip',
            submitId: 'tip',
            submitAt: clone.submitAt,
            content: [{ label: 'tip', value: 1, price: tip }],
            info: { thumb: '', subject: subject(langObj, 'tip') },
            price: {
              info: {
                currency: this.$store.state.common.setting.currency.toUpperCase(),
                price: tip,
                quantity: 1,
                sum: 0,
                total: tip,
                tipLabel: tipLabel,
                tipIncludeTax: tipIncludeTax
              },
              option: null
            }
          })
        }
        this.$store.dispatch('submit/setSubmit', this.order)
      }
    },
    priceFormat (price) {
      const code = this.currencyCode
      return regExp.priceFormat(price, code)
    },
    tipLabel (tip) {
      return `${tip}%`
    },
    tipPrice (tip) {
      const currency = this.currencyCode
      const total = this.order
        ? this.tax && this.order.price.info.tax && this.settingOrder.tipIncludeTax // 세금포함
          ? this.order.price.info.total // 합계+세금
          : this.order.price.info.subtotal || this.order.price.info.total
        : 0
      const price = total * tip / 100
      const priceFormat = regExp.priceFormat(price, currency)
      return regExp.priceUnFormat(priceFormat, currency)
    },
    customTip () {
      const tip = regExp.numberOnly(this.tipInput)
      this.tipInput = tip
    },
    setRoomCharge () {
      this.$router.push({ name: 'payment', params: { ...this.$route.params, payment: 'roomCharge' } })
    },
    setPg (pgType) {
      const supportPg = this.supportPgList && this.supportPgList.find(x => x.pgType === pgType)
      const payment = this.$route.params.payment
      this.$router.push({ name: 'payment', params: { ...this.$route.params, payment: payment, supportPg: supportPg } })
    },
    submit (method) {
      const payment = this.$route.params.payment
      if (method === 'pg') {
        if (this.supportPgList.length > 1) { // 결제 유형(국내결제, 해외결제, 간편결제, ...)
          this.paymentOptionSelector = true
        } else { // 국내결제(normal payment) - 바로 PG 모듈 연결
          const supportPg = this.supportPgList ? { supportPg: this.supportPgList[0] } : null
          this.$router.push({ name: 'payment', params: { ...this.$route.params, payment: payment, ...supportPg } })
        }
      } else if (method === 'roomCharge') {
        if (this.roomchargeMessage) {
          this.orderMessage = true
        } else {
          this.$router.push({ name: 'payment', params: { ...this.$route.params, payment: 'roomCharge' } })
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.payMethod {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 45px;
  margin-bottom: 60px;
  .btnMethod {
    position: relative;
    flex: 1;
    height: 220px;
    padding-top: 15px;
    padding-bottom: 90px;
    font-size: 1.4em;
    line-height: 1.2;
    border: 1px solid $color-lgrey2;
    border-radius: 4px;
    box-shadow: inset 1px 1px 0 $color-white,
    inset -1px -1px 0 $color-lgrey2;
    background-size: 32px;
    background: linear-gradient($color-white, $bg-default);
    & + .btnMethod {
      margin-left: 15px;
    }
    &.disabled {
      color: $color-grey;
      background: rgba($color-lgrey2, 0.5);
    }
    &.btnRoomcharge span {
      display: block;
      margin-top: 8px;
      font-size: 0.8em;
    }
    &:after {
      content: "";
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      height: 90px;
      background-size: 64px;
      background-repeat: no-repeat;
      background-position: 50% 0;
      opacity: 0.7;
    }
    &.btnPg:after {
      background-image: url('~@/assets/img/ico_credit-card.png');
    }
    &.btnRoomcharge:after {
      background-image: url('~@/assets/img/ico_invoice.png');
    }
    &.disabled:after {
      opacity: 0.3;
    }
    span {
      position: relative;
    }
  }
}
.payMessage {
  margin-top: 80px;
  text-align: center;
  font-size: 1.2em;
}
.orderMessage {
  position: relative;
  padding: 30px 15px;
  text-align: center;
  border: 1px solid $color-lgrey2;
  border-radius: 4px;
  .pleaseConfirm{
    margin-bottom: 5px;
    font-size: 12px;
    font-weight: 300;
    color: $color-red;
  }
  &:after {
    content: "";
    position: absolute;
    left: 75%;
    bottom: 100%;
    width: 10px;
    height: 10px;
    margin-bottom: -5px;
    border-left: 1px solid rgba($color-lgrey, 0.7);
    border-top: 1px solid rgba($color-lgrey, 0.7);
    background-color: $color-white;
    transform: rotate(45deg);
  }
}
.tips {
  .tipBox {
    display: flex;
    flex-wrap: wrap;
    margin-left: -2px;
    margin-right: -2px;
    .tipUnit {
      width: calc(33.33% - 4px);
      margin-left: 2px;
      margin-right: 2px;
    }
  }
  .checkbox {
    margin-right: 0;
    margin-bottom: 5px;
    label {
      &:before,
      &:after {
        display: none;
      }
      padding: 0.6em 15px;
      text-align: center;
      color: $color-lgrey;
      border: 1px solid $color-lgrey2;
      border-radius: 4px;
      background: linear-gradient($color-white, $bg-default);
    }
    :checked + label {
      color: $color-white;
      border-color: $color-dark;
      background: $color-dark;
    }
    span {
      display: block;
      font-size: 0.85em;
      opacity: 0.7;
    }
  }
  .custom {
    display: flex;
    .checkbox {
      margin-bottom: 0;
      margin-right: 0;
      label {
        height: 100%;
        padding-top: 0.6em;
        padding-bottom: 0.6em;
        border-left: none;
        border-radius: 0;
        background: $color-white;
      }
      :checked + label {
        background: $color-dgrey;
      }
    }
    .input {
      border-color: $color-lgrey2;
    }
  }
  .customTipPrice {
    span {
      display: inline-block;
      position: relative;
      padding: 2px 8px;
      font-size: 0.85em;
      color: $color-white;
      border-radius: 4px;
      background-color: $color-dgrey;
      &:before {
        content: "";
        position: absolute;
        top: 0;
        left: 20px;
        width: 6px;
        height: 6px;
        margin-top: -3px;
        background-color: $color-dgrey;
        transform: rotate(45deg);
      }
    }
  }
  .sysMessage {
    font-size: 0.85em;
  }
}
.activeMessage {
  .payMethod {
    margin-bottom: 15px;
  }
  .btnPg {
    opacity: 0.3;
  }
}
.payOptionLayer {
  text-align: center;
  .inner {
    max-width: 480px;
    font-size: 1.2em;
  }
  .btns {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    column-gap: 8px;
    row-gap: 8px;
    .btnPg {
      padding: 30px 15px 60px;
      font-size: 1.2em;
      color: $color-dark;
      line-height: 1.2;
      border: 4px solid $color-lgrey2;
      border-radius: 8px;
      background: $color-white url('~@/assets/img/btn_international_pay.png') 100% 100% / auto 100% no-repeat;
      &:active {
        border-color: $color-lgrey;
        box-shadow: inset 1px 1px 5px rgba(0,0,0,0.1);
      }
    }
    [data-simple] {
      grid-column: 1 / 3;
      width: 100%;
      padding: 15px 15px 15px 60px;
      text-align: left;
      font-size: 1em;
      border-width: 2px;
      background-position: 8px 50%;
      background-size: 40px;
    }
    [data-type="NORMAL_PAYMENT"] {
      background-image: url('~@/assets/img/btn_normal_pay.png');
    }
    [data-type="FOREIGN_PAYMENT"] {
      background-image: url('~@/assets/img/btn_international_pay.png');
    }
    [data-type="ALIPAY_SIMPLE_PAYMENT"] {
      background-image: url('~@/assets/img/btn_pg_alipay.png');
    }
    [data-type="WECHAT_SIMPLE_PAYMENT"] {
      background-image: url('~@/assets/img/btn_pg_wechatpay.png');
    }
    [data-type="PAYPAL_SIMPLE_PAYMENT"] {
      background-image: url('~@/assets/img/btn_pg_paypal.png');
    }
  }
}
@media screen and (min-width: 400px) {
  .payOptionLayer {
    .inner {
      padding: 30px;
    }
  }
}
</style>
